import { useState, useEffect } from "react";
import { Layout } from "@/components/Layout";
import { AdminLayout } from "@/components/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { UserAvatar } from "@/components/UserAvatar";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Users,
  Search,
  Filter,
  Calendar,
  MessageSquare,
  Gamepad2,
  Building2,
  Eye,
  ArrowLeft,
  Shield,
  ShieldCheck
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

interface Member {
  id: string;
  display_name: string;
  studio_name?: string | null;
  avatar_url?: string | null;
  user_type: "creator" | "developer" | "player";
  created_at: string;
  bio?: string | null;
  studio_slug?: string | null;
  is_admin?: boolean | null;
  is_banned?: boolean | null;
  is_verified?: boolean | null;
  // Stats
  games_count?: number;
  key_requests_count?: number;
  messages_count?: number;
  last_activity?: string;
}

export default function AdminMembers() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [members, setMembers] = useState<Member[]>([]);
  const [filteredMembers, setFilteredMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [userTypeFilter, setUserTypeFilter] = useState<string>("all");
  const [sortBy, setSortBy] = useState<string>("created_at");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [showNewMembersOnly, setShowNewMembersOnly] = useState(false);

  useEffect(() => {
    if (user?.id) {
      checkAdminAccess();
    }
  }, [user?.id]);

  useEffect(() => {
    if (isAdmin) {
      fetchMembers();
    }
  }, [isAdmin]);

  useEffect(() => {
    filterAndSortMembers();
  }, [members, searchQuery, userTypeFilter, sortBy, sortOrder, showNewMembersOnly]);

  // Mark that admin has visited this page
  useEffect(() => {
    if (isAdmin) {
      localStorage.setItem('admin_members_last_visit', new Date().toISOString());
    }
  }, [isAdmin]);

  const checkAdminAccess = async () => {
    if (!user) return;

    try {
      const { data } = await supabase
        .from("profiles")
        .select("is_admin, display_name")
        .eq("id", user.id)
        .single();

      // Fallback to display_name check if is_admin column doesn't exist yet
      if (data?.is_admin || data?.display_name === 'miikeu') {
        setIsAdmin(true);
      } else {
        toast.error("Access denied. Admin privileges required.");
        navigate("/dashboard");
      }
    } catch (error) {
      console.error("Error checking admin access:", error);
      // Fallback: allow miikeu user even if there's a DB error
      if (user.email?.includes('miikeu') || user.user_metadata?.display_name === 'miikeu') {
        setIsAdmin(true);
      } else {
        toast.error("Error checking admin access");
        navigate("/dashboard");
      }
    }
  };

  const fetchMembers = async () => {
    try {
      setLoading(true);

      // Fetch all profiles with basic info
      const { data: profiles, error: profilesError } = await supabase
        .from("profiles")
        .select(`
          id,
          display_name,
          studio_name,
          avatar_url,
          user_type,
          created_at,
          bio,
          studio_slug,
          is_admin,
          is_banned,
          is_verified,
          active_border_id,
          shop_items(metadata)
        `)
        .order("created_at", { ascending: false });

      if (profilesError) throw profilesError;

      // Fetch additional stats for each member
      const membersWithStats = await Promise.all(
        (profiles || []).map(async (profile) => {
          const [gamesResult, keyRequestsResult, messagesResult] = await Promise.all([
            supabase
              .from("games")
              .select("id")
              .eq("developer_id", profile.id)
              .eq("is_published", true),
            supabase
              .from("key_requests")
              .select("id, created_at")
              .eq("creator_id", profile.id),
            supabase
              .from("messages")
              .select("id, created_at")
              .eq("sender_id", profile.id)
          ]);

          const games_count = gamesResult.data?.length || 0;
          const key_requests_count = keyRequestsResult.data?.length || 0;
          const messages_count = messagesResult.data?.length || 0;

          // Find last activity (most recent message or key request)
          const allActivities = [
            ...(keyRequestsResult.data || []).map(item => ({ date: item.created_at, type: 'key_request' })),
            ...(messagesResult.data || []).map(item => ({ date: item.created_at, type: 'message' }))
          ];

          const lastActivity = allActivities.length > 0
            ? allActivities.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0].date
            : profile.created_at;

          return {
            ...profile,
            games_count,
            key_requests_count,
            messages_count,
            last_activity: lastActivity
          };
        })
      );

      setMembers(membersWithStats);
    } catch (error) {
      console.error("Error fetching members:", error);
      toast.error("Failed to load members");
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortMembers = () => {
    let filtered = [...members];

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(member =>
        member.display_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (member.studio_name && member.studio_name.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    // Filter by user type
    if (userTypeFilter !== "all") {
      filtered = filtered.filter(member => member.user_type === userTypeFilter);
    }

    // Filter by new unverified members (last 2 days)
    if (showNewMembersOnly) {
      const twoDaysAgo = new Date();
      twoDaysAgo.setDate(twoDaysAgo.getDate() - 2);
      filtered = filtered.filter(member => {
        const memberCreatedAt = new Date(member.created_at);
        return memberCreatedAt >= twoDaysAgo && !member.is_verified;
      });
    }

    // Sort
    filtered.sort((a, b) => {
      let aValue: any, bValue: any;

      switch (sortBy) {
        case "display_name":
          aValue = a.display_name;
          bValue = b.display_name;
          break;
        case "created_at":
          aValue = new Date(a.created_at);
          bValue = new Date(b.created_at);
          break;
        case "last_activity":
          aValue = new Date(a.last_activity || a.created_at);
          bValue = new Date(b.last_activity || b.created_at);
          break;
        case "games_count":
          aValue = a.games_count || 0;
          bValue = b.games_count || 0;
          break;
        case "key_requests_count":
          aValue = a.key_requests_count || 0;
          bValue = b.key_requests_count || 0;
          break;
        case "messages_count":
          aValue = a.messages_count || 0;
          bValue = b.messages_count || 0;
          break;
        default:
          aValue = new Date(a.created_at);
          bValue = new Date(b.created_at);
      }

      if (sortOrder === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredMembers(filtered);
  };

  const handleMemberClick = (member: Member) => {
    navigate(`/admin/members/${member.id}`);
  };

  const getDisplayName = (member: Member) => {
    return member.studio_name || member.display_name;
  };

  const getInitial = (member: Member) => {
    const name = getDisplayName(member);
    return name.charAt(0).toUpperCase();
  };


  if (!isAdmin) {
    return (
      <AdminLayout>
        <div className="container mx-auto">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">Access Denied</h1>
            <p className="text-muted-foreground">You don't have permission to access this page.</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (loading) {
    return (
      <AdminLayout>
        <div className="container mx-auto">
          <div className="text-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="mt-2 text-muted-foreground">Loading members...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="container mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold">Manage Members</h1>
              <p className="text-muted-foreground">{filteredMembers.length} members found</p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                  <Input
                    placeholder="Search by name or studio..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="new-members"
                    checked={showNewMembersOnly}
                    onCheckedChange={(checked) => setShowNewMembersOnly(checked === true)}
                  />
                  <label
                    htmlFor="new-members"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                  >
                    New Members
                  </label>
                </div>
              </div>
              <div className="flex gap-2">
                <Select value={userTypeFilter} onValueChange={setUserTypeFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="creator">Creators</SelectItem>
                    <SelectItem value="developer">Developers</SelectItem>
                    <SelectItem value="player">Players</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="created_at">Join Date</SelectItem>
                    <SelectItem value="display_name">Name</SelectItem>
                    <SelectItem value="last_activity">Last Activity</SelectItem>
                    <SelectItem value="games_count">Games</SelectItem>
                    <SelectItem value="key_requests_count">Key Requests</SelectItem>
                    <SelectItem value="messages_count">Messages</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
                >
                  {sortOrder === "asc" ? "↑" : "↓"}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Members List */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredMembers.map((member) => (
            <Card
              key={member.id}
              className="cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => handleMemberClick(member)}
            >
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <UserAvatar
                    src={member.avatar_url || undefined}
                    fallback={getInitial(member)}
                    className="w-12 h-12"
                    borderStyle={(member as any).shop_items?.metadata?.style}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold truncate">{getDisplayName(member)}</h3>
                      <Badge variant={member.user_type === "developer" ? "default" : member.user_type === "creator" ? "secondary" : "outline"}>
                        {member.user_type === "developer" ? (
                          <>
                            <Building2 className="w-3 h-3 mr-1" />
                            Developer
                          </>
                        ) : member.user_type === "creator" ? (
                          <>
                            <Users className="w-3 h-3 mr-1" />
                            Creator
                          </>
                        ) : (
                          <>
                            <Gamepad2 className="w-3 h-3 mr-1" />
                            Player
                          </>
                        )}
                      </Badge>
                      {member.is_admin && (
                        <Badge variant="destructive" className="bg-purple-500 hover:bg-purple-600">
                          <ShieldCheck className="w-3 h-3 mr-1" />
                          Admin
                        </Badge>
                      )}
                    </div>
                    {member.studio_name && (
                      <p className="text-sm text-muted-foreground truncate">
                        {member.display_name}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground mb-3">
                      Joined {new Date(member.created_at).toLocaleDateString()}
                    </p>

                    <div className={`grid gap-2 text-xs ${member.user_type === 'developer' ? 'grid-cols-2' : 'grid-cols-2'}`}>
                      {member.user_type === 'developer' ? (
                        <>
                          <div className="text-center">
                            <div className="font-medium">{member.games_count || 0}</div>
                            <div className="text-muted-foreground">Games</div>
                          </div>
                          <div className="text-center">
                            <div className="font-medium">{member.messages_count || 0}</div>
                            <div className="text-muted-foreground">Messages</div>
                          </div>
                        </>
                      ) : member.user_type === 'creator' ? (
                        <>
                          <div className="text-center">
                            <div className="font-medium">{member.key_requests_count || 0}</div>
                            <div className="text-muted-foreground">Requests</div>
                          </div>
                          <div className="text-center">
                            <div className="font-medium">{member.messages_count || 0}</div>
                            <div className="text-muted-foreground">Messages</div>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="text-center">
                            <div className="font-medium">{member.messages_count || 0}</div>
                            <div className="text-muted-foreground">Messages</div>
                          </div>
                          <div className="text-center">
                            <div className="font-medium">-</div>
                            <div className="text-muted-foreground">Activity</div>
                          </div>
                        </>
                      )}
                    </div>

                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredMembers.length === 0 && (
          <Card className="p-8 text-center">
            <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No members found</h3>
            <p className="text-muted-foreground">
              Try adjusting your search or filter criteria
            </p>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
}
